import React, { useEffect, useState } from 'react';
import {
    Box, CircularProgress, Typography, Select, MenuItem,
    Checkbox, FormControlLabel, IconButton, makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { listProductDefinitionsService } from '../../../services/pac.service';

const MODES = [
    { value: '', label: 'Not in plan' },
    { value: 'active', label: 'Active (on by default)' },
    { value: 'available', label: 'Available (activatable)' },
];
const FULL = { view: true, create: true, link: true };

const CARD_STYLES = {
    active: { background: '#ecfdf5', border: '1px solid #6ee7b7', titleColor: '#047857' },
    available: { background: '#eff6ff', border: '1px solid #93c5fd', titleColor: '#1d4ed8' },
    default: { background: '#f9fafb', border: '1px solid #d1d5db', titleColor: '#374151' },
};

const useStyles = makeStyles((theme) => ({
    addBar: {
        marginBottom: theme.spacing(1.5),
    },
    addSelect: {
        width: '100%',
    },
    cardGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: theme.spacing(1.5),
    },
    card: {
        borderRadius: 6,
        padding: theme.spacing(1.25, 1.5),
        position: 'relative',
        overflow: 'hidden',
        minWidth: 0,
    },
    cardHeader: {
        marginBottom: theme.spacing(1),
        paddingRight: theme.spacing(2.5),
    },
    cardTitle: {
        fontWeight: 600,
        fontSize: '0.8125rem',
        lineHeight: 1.25,
    },
    cardKey: {
        fontSize: '0.6875rem',
        color: theme.palette.text.secondary,
        marginTop: 1,
        wordBreak: 'break-all',
    },
    removeBtn: {
        position: 'absolute',
        top: 2,
        right: 2,
        padding: 2,
    },
    permRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: theme.spacing(0.25),
        marginTop: theme.spacing(1),
        width: '100%',
    },
    permLabel: {
        marginRight: 0,
        marginLeft: -6,
        minWidth: 0,
        '& .MuiFormControlLabel-label': {
            fontSize: '0.75rem',
        },
    },
    emptyHint: {
        padding: theme.spacing(1.5),
        textAlign: 'center',
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        border: `1px dashed ${theme.palette.divider}`,
        borderRadius: 6,
    },
}));

/** Backend shape: { [product_key]: { mode: 'active'|'available', permissions: { view, create, link } } } */
export const normalizePacProducts = (val) => {
    if (!val || typeof val !== 'object' || Array.isArray(val)) return {};
    const out = {};
    Object.entries(val).forEach(([key, entry]) => {
        if (entry === null || entry === undefined) return;
        if (typeof entry !== 'object') return;
        if (entry.mode === 'active' || entry.mode === 'available') {
            out[key] = {
                mode: entry.mode,
                permissions: {
                    view: entry.permissions?.view !== false,
                    create: entry.permissions?.create !== false,
                    link: entry.permissions?.link !== false,
                },
            };
        }
    });
    return out;
};

const getCardStyle = (mode) => {
    if (mode === 'active') return CARD_STYLES.active;
    if (mode === 'available') return CARD_STYLES.available;
    return CARD_STYLES.default;
};

/**
 * Per-plan product matrix editor. For each product choose a mode
 * (Not in plan / Active / Available) and the view/create/link permissions
 * granted when the product is on.
 */
const PacProductsPicker = ({ value, onChange, disabled, helperText }) => {
    const classes = useStyles();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const map = normalizePacProducts(value);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        listProductDefinitionsService()
            .then((res) => {
                if (!mounted) return;
                const list = (res && res.data && res.data.products) ? res.data.products : [];
                setProducts(list.filter((p) => p.is_active !== false));
            })
            .catch(() => { if (mounted) setProducts([]); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, []);

    const setMode = (key, mode) => {
        const next = { ...map };
        if (!mode) delete next[key];
        else next[key] = { mode, permissions: map[key]?.permissions || { ...FULL } };
        onChange(next);
    };

    const setPerm = (key, dim, checked) => {
        if (!map[key]) return;
        const next = { ...map, [key]: { ...map[key], permissions: { ...map[key].permissions, [dim]: checked } } };
        onChange(next);
    };

    const addedKeys = Object.keys(map);
    const availableToAdd = products.filter((p) => !map[p.product_key]);

    const handlePickProduct = (key) => {
        if (!key || disabled) return;
        setMode(key, 'active');
    };

    const resolveProduct = (key) => products.find((p) => p.product_key === key);

    if (loading) {
        return (
            <Box display="flex" alignItems="center" style={{ gap: 8, padding: '8px 0' }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="textSecondary">Loading products…</Typography>
            </Box>
        );
    }
    if (products.length === 0) {
        return <Typography variant="body2" color="textSecondary">No products found. Add products under the Products tab first.</Typography>;
    }

    return (
        <Box>
            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                Products in this package
            </Typography>

            {availableToAdd.length > 0 && (
                <Box className={classes.addBar}>
                    <Select
                        className={classes.addSelect}
                        displayEmpty
                        variant="outlined"
                        size="small"
                        value=""
                        disabled={disabled}
                        onChange={(e) => handlePickProduct(e.target.value)}
                    >
                        <MenuItem value=""><em>— Add a product —</em></MenuItem>
                        {availableToAdd.map((p) => (
                            <MenuItem key={p.product_key} value={p.product_key}>{p.label}</MenuItem>
                        ))}
                    </Select>
                </Box>
            )}

            {addedKeys.length === 0 ? (
                <Typography variant="body2" className={classes.emptyHint}>
                    No products added yet. Choose one from the dropdown above.
                </Typography>
            ) : (
                <Box className={classes.cardGrid}>
                    {addedKeys.map((key) => {
                        const p = resolveProduct(key);
                        const entry = map[key];
                        const mode = entry?.mode || '';
                        const perms = entry?.permissions || FULL;
                        const cardStyle = getCardStyle(mode);
                        const label = p?.label || key;

                        return (
                            <Box
                                key={key}
                                className={classes.card}
                                style={{ background: cardStyle.background, border: cardStyle.border }}
                            >
                                <IconButton
                                    className={classes.removeBtn}
                                    size="small"
                                    disabled={disabled}
                                    aria-label="Remove product"
                                    onClick={() => setMode(key, '')}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>

                                <Box className={classes.cardHeader}>
                                    <Box>
                                        <Typography className={classes.cardTitle} style={{ color: cardStyle.titleColor }}>
                                            {label}
                                        </Typography>
                                        <Typography className={classes.cardKey}>{key}</Typography>
                                    </Box>
                                </Box>

                                <Select
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={mode}
                                    disabled={disabled}
                                    onChange={(e) => setMode(key, e.target.value)}
                                >
                                    {MODES.map((m) => (
                                        <MenuItem key={String(m.value)} value={m.value}>{m.label}</MenuItem>
                                    ))}
                                </Select>

                                <Box className={classes.permRow}>
                                    {['view', 'create', 'link'].map((dim) => (
                                        <FormControlLabel
                                            key={dim}
                                            className={classes.permLabel}
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    color="primary"
                                                    checked={!!mode && perms[dim] !== false}
                                                    disabled={disabled || !mode}
                                                    onChange={(e) => setPerm(key, dim, e.target.checked)}
                                                />
                                            }
                                            label={dim.charAt(0).toUpperCase() + dim.slice(1)}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            )}

            {helperText && (
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                    {helperText}
                </Typography>
            )}
        </Box>
    );
};

export default PacProductsPicker;
